import React, { useState } from 'react';
import FeatherIcon from './FeatherIcon';
import { hubSpotService } from '../services/hubspotService';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  // Newsletter subscription state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState<'success' | 'error' | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };

  /**
   * @description Handles contact form submission
   * @param e - Form submit event
   * @validation Validates required fields before submission
   * @api Submits to HubSpot contact form
   * @accessibility Provides success/error feedback to screen readers
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await hubSpotService.submitContactForm(formData);
      setSubmitStatus('success');
      setFormData({ firstName: '', lastName: '', email: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * @description Handles newsletter subscription
   * @param e - Form submit event
   * @validation Validates email format before submission
   * @api Creates HubSpot contact from the newsletter form
   * @accessibility Provides success/error feedback to screen readers
   */
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;

    setIsNewsletterSubmitting(true);
    setNewsletterStatus(null);

    try {
      await hubSpotService.subscribeToNewsletter(newsletterEmail);
      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterStatus('error');
    } finally {
      setIsNewsletterSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4 text-gray-800">Get In Touch</h2>
          <p className="text-xl max-w-3xl mx-auto text-gray-700">
            Have questions or want to get involved? We'd love to hear from you.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-10">
          <div className="md:w-1/2">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <FeatherIcon name="check-circle" className="text-green-500 mr-2" />
                    <span className="text-green-700 font-medium">Thank you! Your message has been sent successfully.</span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <FeatherIcon name="x-circle" className="text-red-500 mr-2" />
                    <span className="text-red-700 font-medium">Sorry, there was an error sending your message. Please try again.</span>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="firstName" className="block mb-2 font-medium text-gray-800">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block mb-2 font-medium text-gray-800">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="email" className="block mb-2 font-medium text-gray-800">Email</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="message" className="block mb-2 font-medium text-gray-800">Message</label>
                <textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary px-8 py-3 rounded-full font-medium w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
          <div className="md:w-1/2">
            <div className="card p-8 h-full">
              <h3 className="text-xl font-bold mb-6 text-gray-800">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <FeatherIcon name="mail" className="text-primary mr-4 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1 text-gray-800">Email</h4>
                    <p className="text-gray-700">info@pawsitivefuture.org</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FeatherIcon name="phone" className="text-primary mr-4 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1 text-gray-800">Phone</h4>
                    <p className="text-gray-700">(213) 729-1614</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FeatherIcon name="map-pin" className="text-primary mr-4 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1 text-gray-800">Address</h4>
                    <p className="text-gray-700">
                      8605 Santa Monica Blvd PMB 707825<br />
                      West Hollywood, CA 90069-4109
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-xl font-bold mb-6 text-gray-800">Newsletter Signup</h3>
                <p className="mb-4 text-gray-700">Stay updated on our programs and impact.</p>

                {newsletterStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                      <FeatherIcon name="check-circle" className="text-green-500 mr-2" />
                      <span className="text-green-700 font-medium">Successfully subscribed to our newsletter!</span>
                    </div>
                  </div>
                )}

                {newsletterStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                      <FeatherIcon name="x-circle" className="text-red-500 mr-2" />
                      <span className="text-red-700 font-medium">Sorry, there was an error with your subscription. Please try again.</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 sm:gap-0">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    disabled={isNewsletterSubmitting}
                    className="w-full sm:flex-grow sm:min-w-0 px-4 py-3 border border-gray-300 rounded-lg sm:rounded-r-none sm:rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                    aria-label="Email address for newsletter subscription"
                  />
                  <button
                    type="submit"
                    disabled={isNewsletterSubmitting || !newsletterEmail.trim()}
                    className="btn-primary w-full sm:w-auto sm:flex-shrink-0 whitespace-nowrap px-6 py-3 rounded-lg sm:rounded-l-none sm:rounded-r-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Subscribe to newsletter"
                  >
                    {isNewsletterSubmitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;